import { FC, memo, useCallback, useRef, useState } from 'react'
import s from './Select.module.sass'
import { getOptions, getSelectLabel, isClassPlaceholder } from './helpers'
import useOutsideClick from '@shared/lib/hooks/useOutsideClick'
import { FieldSelect, SelectOption } from '@shared/types/fields/fields'
import { getErrorData } from '@shared/lib/fields/getErrorData'
import IconArrow from '@icons/icon-arrow-small.svg'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { useCurrentSize } from '@shared/lib/helpers/classes'

type SelectMode = 'white' | 'white-border' | 'white-shadow' | 'yellow-border' | 'black' | 'red-link' | 'ghost'

interface Props {
  field: FieldSelect
  handleChange: (option: SelectOption) => void
  hideDisabledValue?: boolean
  className?: string
  dropdownMaxHeight?: number
  sizes?: string
  mode?: SelectMode
}

const Select: FC<Props & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> = ({
  field,
  handleChange,
  hideDisabledValue = false,
  className = '',
  dropdownMaxHeight,
  sizes,
  mode,
  ...props
}) => {
  const option = getOptions(field)
  const errorData = getErrorData(field)
  const [size] = useCurrentSize(sizes ?? 'M')
  const hasSize = sizes != null && sizes !== ''
  const hasMode = mode != null

  const ref = useRef<HTMLDivElement | null>(null)
  const [isDropDown, setDropDown] = useState(false)
  const close = useCallback(() => setDropDown(false), [])
  useOutsideClick({ ref, handler: close, onEscape: close })

  const onClickSelect = () => !field.disabled && setDropDown((v) => !v)
  const handleClick = (option: SelectOption) => {
    if (!option.disabled) {
      const currentValue = field.value

      setDropDown(false)
      if (option.value !== currentValue?.value) {
        handleChange(option)
      }
    }
  }

  return (
    <BlockBoundary>
      <div ref={ref} id={field.name} className={s.wrapper} {...props}>
        <div
          id={'field-select-input'}
          className={`${s.field} ${errorData.class} ${className}`}
          onClick={onClickSelect}
          data-disabled={field.disabled}
          data-hide-disabled-value={hideDisabledValue}
          {...(hasSize && { 'data-size': size })}
          {...(hasMode && { 'data-mode': mode })}>
          <span
            className={`${s.field__value} ${s[isClassPlaceholder(field) ? 'field__value--placeholder' : '']}`}
            data-empty={Boolean(field.value?.isEmpty)}>
            {getSelectLabel(field)}
          </span>
          <button type='button' className={s.field__arrow} data-close={isDropDown}>
            <IconArrow />
          </button>
        </div>
        {!!option?.length && (
          <div
            id={'field-select-dropdown'}
            className={s.dropdown}
            data-visible={isDropDown}
            style={dropdownMaxHeight ? { maxHeight: dropdownMaxHeight } : undefined}>
            <div className={s.dropdown__shadow}></div>
            <ul className={s.dropdown__list} role='menu' aria-labelledby='dropdownMenu'>
              {option.map((el: SelectOption) => (
                <li
                  key={el.value}
                  className={s.dropdown__item}
                  role='presentation'
                  data-disabled={el.disabled}
                  data-empty={el.isEmpty}
                  onClick={() => handleClick(el)}>
                  <span className={s.dropdown__link} role='menuitem'>
                    {el.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </BlockBoundary>
  )
}

export default memo(Select)
